import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Server,
  Plus,
  Trash2,
  ChevronRight,
  Loader2,
  ChevronDown,
} from "lucide-react";
import ConfirmDialog from "../common/ConfirmDialog";
import {
  Card,
  EmptyState,
  Notice,
  PrimaryButton,
  SecondaryButton,
  fmtDateTime,
} from "./parts";

/** A main-process error ({ code, message }) as a sentence for the user. */
export function errorText(t, error) {
  if (!error) return "";
  switch (error.code) {
    case "auth":
      return t("caseDashboard.errors.auth");
    case "auth-key":
      return t("caseDashboard.errors.authKey");
    case "no-key":
      return t("caseDashboard.errors.noKey");
    case "not-found":
      return t("caseDashboard.errors.notFound");
    case "refused":
      return t("caseDashboard.errors.refused");
    case "timeout":
      return t("caseDashboard.errors.timeout");
    case "host-key":
      return t("caseDashboard.errors.hostKey");
    case "input":
      return t("caseDashboard.errors.input");
    case "pair":
      return error.message === "not-found"
        ? t("caseDashboard.errors.pairNotFound")
        : t("caseDashboard.errors.pair", { message: error.message });
    default:
      return t("caseDashboard.errors.generic", {
        message: error.message || String(error),
      });
  }
}

const inputClass =
  "w-full rounded-[10px] border border-[#E5E7EB] px-4 py-2.5 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition duration-150 focus:border-[#E07A3A] focus:shadow-[0_0_0_3px_rgba(224,122,58,0.1)]";

const Field = ({ label, help, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-[#6B7280]">
      {label}
    </span>
    {children}
    {help && (
      <span className="mt-1 block text-[11px] text-[#9CA3AF]">{help}</span>
    )}
  </label>
);

const AddCaseForm = ({ onAdd, onCancel }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    host: "",
    username: "sitcase",
    port: "22",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.host.trim()) {
      setError({ code: "input" });
      return;
    }
    setBusy(true);
    setError(null);
    const res = await onAdd({ ...form, port: Number(form.port) || 22 });
    setBusy(false);
    if (!res?.ok) setError(res?.error || { code: "error", message: "" });
  };

  return (
    <Card title={t("caseDashboard.add.title")}>
      <form onSubmit={submit} className="space-y-4 p-4">
        <p className="text-[13px] text-[#6B7280]">
          {t("caseDashboard.add.intro")}
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={t("caseDashboard.add.name")}>
            <input
              className={inputClass}
              value={form.name}
              onChange={set("name")}
              placeholder={t("caseDashboard.add.namePlaceholder")}
            />
          </Field>
          <Field
            label={t("caseDashboard.add.host")}
            help={t("caseDashboard.add.hostHelp")}
          >
            <input
              className={inputClass}
              value={form.host}
              onChange={set("host")}
              placeholder={t("caseDashboard.add.hostPlaceholder")}
              autoCapitalize="off"
              spellCheck={false}
            />
          </Field>
          <Field
            label={t("caseDashboard.add.password")}
            help={t("caseDashboard.add.passwordHelp")}
          >
            <input
              className={inputClass}
              type="password"
              value={form.password}
              onChange={set("password")}
              autoComplete="off"
            />
          </Field>
        </div>
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-[#6B7280] hover:text-[#374151]"
        >
          <ChevronDown
            size={14}
            className={`transition duration-150 ${
              advanced ? "rotate-180" : ""
            }`}
          />
          {t("caseDashboard.add.advanced")}
        </button>
        {advanced && (
          <div className="grid gap-4 md:grid-cols-2">
            <Field label={t("caseDashboard.add.username")}>
              <input
                className={inputClass}
                value={form.username}
                onChange={set("username")}
              />
            </Field>
            <Field label={t("caseDashboard.add.port")}>
              <input
                className={inputClass}
                value={form.port}
                onChange={set("port")}
                inputMode="numeric"
              />
            </Field>
          </div>
        )}
        {error && <Notice tone="error">{errorText(t, error)}</Notice>}
        <div className="flex justify-end gap-2">
          <SecondaryButton onClick={onCancel} disabled={busy}>
            {t("caseDashboard.add.cancel")}
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={busy}>
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy
              ? t("caseDashboard.add.pairing")
              : t("caseDashboard.add.submit")}
          </PrimaryButton>
        </div>
      </form>
    </Card>
  );
};

const CasesView = ({ list, onOpen }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const showForm = adding || (!list.loading && list.cases.length === 0);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827]">
            {t("caseDashboard.cases.title")}
          </h1>
          <p className="text-[13px] text-[#6B7280]">
            {t("caseDashboard.cases.subtitle")}
          </p>
        </div>
        {!showForm && (
          <PrimaryButton onClick={() => setAdding(true)}>
            <Plus size={14} />
            {t("caseDashboard.cases.add")}
          </PrimaryButton>
        )}
      </div>

      {showForm && (
        <AddCaseForm
          onAdd={async (input) => {
            const res = await list.add(input);
            if (res?.ok) setAdding(false);
            return res;
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {list.cases.length > 0 && (
        <Card>
          {list.cases.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3 transition duration-150 last:border-b-0 hover:bg-[#FAFAFA]"
            >
              <button
                type="button"
                onClick={() => onOpen(c.id)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6]">
                  <Server size={18} className="text-[#E07A3A]" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#111827]">
                    {c.name}
                  </p>
                  <p className="truncate text-xs text-[#6B7280]">
                    {c.host}
                    {c.pairedAt &&
                      ` · ${t("caseDashboard.cases.added", {
                        date: fmtDateTime(c.pairedAt),
                      })}`}
                  </p>
                </div>
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRemoving(c)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] transition duration-150 hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                  aria-label={t("caseDashboard.cases.remove")}
                  title={t("caseDashboard.cases.remove")}
                >
                  <Trash2 size={15} />
                </button>
                <SecondaryButton onClick={() => onOpen(c.id)}>
                  {t("caseDashboard.cases.open")}
                  <ChevronRight size={14} />
                </SecondaryButton>
              </div>
            </div>
          ))}
        </Card>
      )}

      {!showForm && !list.loading && list.cases.length === 0 && (
        <EmptyState icon={Server} title={t("caseDashboard.cases.empty")} />
      )}

      <ConfirmDialog
        open={!!removing}
        title={t("caseDashboard.cases.removeTitle", { name: removing?.name })}
        body={t("caseDashboard.cases.removeBody")}
        confirmLabel={t("caseDashboard.cases.remove")}
        danger
        onConfirm={async () => {
          const id = removing.id;
          setRemoving(null);
          await list.remove(id);
        }}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
};

export default CasesView;
